import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (e: React.FormEvent) => void;
}

export const SearchBar = ({ searchQuery, setSearchQuery, handleSearch }: SearchBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // ⌘K / Ctrl+K shortcut → focus the search bar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Live-navigate to /search as the user types (debounced)
  useEffect(() => {
    if (!focused) return;
    const q = searchQuery.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => {
      const target = `/search?q=${encodeURIComponent(q)}`;
      if (location.pathname === "/search") {
        navigate(target, { replace: true });
      } else {
        navigate(target);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, focused, navigate, location.pathname]);

  return (
    <form onSubmit={handleSearch} className="flex-1 max-w-xl">
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Search people, places, tips, reviews…"
          className="w-full pl-10 pr-16 h-10 rounded-full bg-muted/50 border-transparent focus-visible:bg-background focus-visible:border-input transition-colors"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="p-1 rounded-full hover:bg-muted text-muted-foreground"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <kbd className="hidden lg:inline-flex items-center px-1.5 h-5 text-[10px] font-medium text-muted-foreground bg-background rounded border border-border">
            ⌘K
          </kbd>
        </div>
      </div>
    </form>
  );
};
